import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useApiResource } from '../../lib/useApiResource';
import { StatusBadge } from '../../components/StatusBadge';
import { listPrompts, readPrompt } from './api';
import { PromptDetailPanel } from './PromptDetail';
import type { PromptDetail, PromptSummary } from './types';

/**
 * ADM ops — Prompt Studio.
 *
 * Every AI prompt in the product used to be a TypeScript template literal, so
 * changing a word of what Dr. Lynkia says meant a code change, a review and a
 * deploy. They now live in Firestore, and this is what makes them tunable by
 * the people who can judge the output rather than only by whoever can ship a
 * release.
 *
 * The lifecycle is deliberately two states. A draft reaches nobody; a published
 * version is what patients are getting. Publishing demotes the previous version
 * to a draft rather than discarding it, so a bad prompt is undone by publishing
 * the old one again, and unpublishing entirely falls back to the text compiled
 * into the running release — there is always a way back that does not involve
 * retyping a prompt from memory.
 *
 * What the screen refuses to do is as important as what it does. It will not
 * let a version be saved that drops a placeholder or one of the literal tokens
 * backend code parses out of the model's reply, because that failure is silent:
 * service cards simply stop appearing, and nothing anywhere logs why.
 *
 * Shape: a sticky pick-rail beside a detail pane. Prompts run to ~7 KB, so the
 * editor needs the full width of the page, and the list has to stay reachable
 * without scrolling a long prompt back to the top to change which one is open.
 */
export function PromptStudioPage() {
  const { data: prompts, loading, error, reload } = useApiResource<PromptSummary[]>(listPrompts, []);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<PromptDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const loadDetail = (key: string) => {
    setDetailError(null);
    readPrompt(key)
      .then(setDetail)
      .catch(() => setDetailError('Could not load this prompt.'));
  };

  useEffect(() => {
    if (selectedKey) loadDetail(selectedKey);
    else setDetail(null);
  }, [selectedKey]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return prompts ?? [];
    return (prompts ?? []).filter(
      (p) =>
        p.definition.key.toLowerCase().includes(needle) ||
        p.definition.description.toLowerCase().includes(needle),
    );
  }, [prompts, query]);

  const total = prompts?.length ?? 0;
  const publishedCount = (prompts ?? []).filter((p) => p.publishedVersionId).length;

  if (loading) {
    return (
      <div className="empty-state" data-testid="prompt-studio-loading">
        Loading prompts…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="prompt-studio-error">
        <div className="error-banner">{error}</div>
        <button type="button" onClick={reload} className="btn btn-secondary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Prompt Studio</h1>
          <p>
            Every AI prompt the product runs on. Drafts reach nobody; publishing takes effect
            immediately, with no deploy. Unpublishing reverts a prompt to the text shipped in the
            current release.
          </p>
        </div>
        <div className="dashboard-links">
          <StatusBadge label={`${publishedCount} published`} tone={publishedCount ? 'good' : 'neutral'} />
          <StatusBadge label={`${total - publishedCount} on shipped text`} />
        </div>
      </div>

      <div className="ps-workbench">
        <aside className="ps-rail">
          <div className="ps-rail-head">
            <div className="ps-rail-search">
              <Search size={13} aria-hidden />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by key or description…"
                aria-label="Filter prompts"
                data-testid="prompt-studio-filter"
              />
            </div>
            <p className="ps-rail-count">
              {filtered.length === total
                ? `${total} prompt${total === 1 ? '' : 's'}`
                : `${filtered.length} of ${total}`}
            </p>
          </div>

          <div className="ps-rail-list">
            {filtered.length === 0 && (
              <p className="ps-rail-empty">No prompt matches that filter.</p>
            )}

            {filtered.map((prompt) => {
              const isSelected = prompt.definition.key === selectedKey;
              const live = !!prompt.publishedVersionId;
              return (
                <button
                  key={prompt.definition.key}
                  type="button"
                  onClick={() => setSelectedKey(prompt.definition.key)}
                  aria-current={isSelected}
                  data-testid={`prompt-row-${prompt.definition.key}`}
                  className={`ps-rail-item${isSelected ? ' active' : ''}`}
                >
                  <span className="ps-rail-item-top">
                    <span className="ps-rail-key">{prompt.definition.key}</span>
                    {/* The dot is decoration over a fact stated in the title, so it is
                        hidden from assistive tech rather than announced as "dot". */}
                    <span
                      className={`ps-dot${live ? ' live' : ''}`}
                      title={live ? 'Running on a published version' : 'Running on the shipped text'}
                      aria-hidden
                    />
                  </span>
                  <p className="ps-rail-desc">{prompt.definition.description}</p>
                </button>
              );
            })}
          </div>
        </aside>

        <section>
          {detailError && (
            <div className="error-banner" data-testid="prompt-detail-load-error">
              {detailError}
            </div>
          )}

          {!selectedKey && (
            <div className="form-card full-width">
              <div className="empty-state" data-testid="prompt-studio-empty">
                Select a prompt to see its contract, its history, and what it is running on.
              </div>
            </div>
          )}

          {detail && (
            <PromptDetailPanel
              detail={detail}
              onChanged={() => {
                loadDetail(detail.definition.key);
                reload();
              }}
            />
          )}
        </section>
      </div>
    </div>
  );
}
